import classNames from "classnames";
import { FC, memo } from "react";
import { Link } from "react-router-dom";

import { ReactComponent as InfoIcon } from "assets/images/common/info.svg";

import Tag, { TagTone } from "modules/assets/Tag";

import styles from "./style.module.scss";

type MetricTone = "green" | "orange" | "red" | "gray";

type Pill = { tone: TagTone; label: string };

type MetricSubInput = { label: string; value: string; unit?: string };

export type AirQualityMetric = {
  tone: MetricTone;
  value: string;
  badge?: Pill;
  info?: string;
  label: string;
  tag: Pill;
  subInputs?: MetricSubInput[];
  delta?: { label: string; value: string; unit?: string };
  progress?: number;
  limit?: string;
};

export type AirQualityCardData = {
  formula: string;
  unit: string;
  unitInfo?: string;
  name?: string;
  sensor?: { id: string; location: string };
  status: Pill;
  realtimeLabel: string;
  realtimeValue: string;
  realtimeUnit: string;
  complianceLabel: string;
  metrics: AirQualityMetric[];
  footerNote?: string;
  updatedNote: string;
  outdatedNote?: string;
};

type PropTypes = {
  data: AirQualityCardData;
  to?: string;
  className?: string;
};

const AirQualityCard: FC<PropTypes> = ({ data, to, className }) => {
  return (
    <article className={classNames(styles.card, className)}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <div className={styles.title}>
            <span className={styles.formula}>{data.formula}</span>

            <span className={styles.unitGroup}>
              <span className={styles.unit}>{data.unit}</span>
              {data.unitInfo && (
                <span className={styles.info} tabIndex={0}>
                  <InfoIcon />
                  <span className={classNames(styles.infoTooltip, styles.unitTooltip)}>{data.unitInfo}</span>
                </span>
              )}
            </span>
          </div>

          {data.sensor ? (
            <div className={styles.sensorMeta}>
              <span className={styles.name}>Sensor: {data.sensor.id}</span>
              <span className={styles.name}>Location: {data.sensor.location}</span>
            </div>
          ) : (
            data.name && <span className={styles.name}>{data.name}</span>
          )}
        </div>

        <Tag tone={data.status.tone}>{data.status.label}</Tag>
      </header>

      <div className={styles.realtime}>
        <span className={styles.realtimeLabel}>{data.realtimeLabel}</span>

        <div className={styles.realtimeValue}>
          <span className={styles.realtimeNumber}>{data.realtimeValue}</span>
          <span className={styles.realtimeUnit}>{data.realtimeUnit}</span>
        </div>
      </div>

      <div className={styles.metrics}>
        <span className={styles.metricsTitle}>{data.complianceLabel}</span>

        {data.metrics.map((metric, index) => (
          <div key={`${metric.label}-${index}`} className={classNames(styles.block, styles[`block-${metric.tone}`])}>
            <div className={styles.blockHead}>
              <div className={styles.blockValueRow}>
                <span className={styles.valueGroup}>
                  <span className={classNames(styles.blockValue, styles[`value-${metric.tone}`])}>{metric.value}</span>
                  {metric.info && (
                    <span className={styles.info} tabIndex={0}>
                      <InfoIcon />
                      <span className={styles.infoTooltip}>{metric.info}</span>
                    </span>
                  )}
                </span>
                {metric.badge && <Tag tone={metric.badge.tone}>{metric.badge.label}</Tag>}
              </div>

              <div className={styles.blockLabelRow}>
                <span className={styles.blockLabel}>{metric.label}</span>
                <Tag tone={metric.tag.tone}>{metric.tag.label}</Tag>
              </div>
            </div>

            {metric.subInputs && (
              <div className={styles.subInputs}>
                {metric.subInputs.map((sub) => (
                  <div key={sub.label} className={styles.subInput}>
                    <span className={styles.subInputLabel}>{sub.label}</span>

                    <div className={styles.subInputValue}>
                      <span className={styles.subInputNumber}>{sub.value}</span>
                      {sub.unit && <span className={styles.subInputUnit}>{sub.unit}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {metric.delta && (
              <div className={classNames(styles.delta, styles[`delta-${metric.tone}`])}>
                <span className={styles.deltaLabel}>{metric.delta.label}</span>

                <div className={styles.deltaValue}>
                  <span className={styles.deltaNumber}>{metric.delta.value}</span>
                  {metric.delta.unit && <span className={styles.deltaUnit}>{metric.delta.unit}</span>}
                </div>
              </div>
            )}

            {typeof metric.progress === "number" && (
              <div className={classNames(styles.track, styles[`track-${metric.tone}`])}>
                <span
                  className={classNames(styles.fill, styles[`fill-${metric.tone}`])}
                  style={{ width: `${Math.min(100, metric.progress)}%` }}
                />
              </div>
            )}

            {metric.limit && <span className={styles.limit}>{metric.limit}</span>}
          </div>
        ))}
      </div>

      <footer className={styles.footer}>
        {data.footerNote &&
          (to ? (
            <Link to={to} className={styles.footerNote}>
              {data.footerNote}
            </Link>
          ) : (
            <span className={styles.footerNote}>{data.footerNote}</span>
          ))}
        <span className={styles.footerUpdated}>
          {data.updatedNote}
          {data.outdatedNote && <span className={styles.footerOutdated}>{data.outdatedNote}</span>}
        </span>
      </footer>
    </article>
  );
};

export default memo(AirQualityCard);
